import React, { useState, useEffect } from 'react';
import { X, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { portalDb } from '../utils/portalFirebase'; // Import portalDb
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../utils/firebase';

const NotificationBar = () => {
  const [visible, setVisible] = useState(true);
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch unread messages
  useEffect(() => {
    if (!user) return;
    
    setIsLoading(true);
    
    // Create a query for the first unread message
    const messagesRef = collection(portalDb, 'reportMessages');
    const messagesQuery = query(
      messagesRef,
      where('read', '==', false),
      where('senderId', '!=', user.uid), // Not from the current user
      orderBy('senderId'), // Needed for inequality filter
      orderBy('timestamp', 'desc'),
      limit(1)
    );
    
    // Listen for real-time updates
    const unsubscribe = onSnapshot(messagesQuery, async (querySnapshot) => {
      if (querySnapshot.empty) {
        setNotification(null);
        setIsLoading(false);
        return;
      }
      
      // Get the first unread message
      const messageDoc = querySnapshot.docs[0];
      const messageData = messageDoc.data();
      
      // Get report details
      try {
        const reportRef = doc(db, 'inspections', messageData.reportId);
        const reportDoc = await getDoc(reportRef);
        
        if (reportDoc.exists()) {
          const reportData = reportDoc.data();
          
          // Get sender name (if it's in the message or use client name from report)
          const senderName = messageData.senderName || 'Client';
          const clientName = reportData.clientName || 'Client';
          
          setNotification({
            id: messageDoc.id,
            reportId: messageData.reportId,
            senderName: senderName,
            clientName: clientName,
            timestamp: messageData.timestamp?.toDate() || new Date()
          });
        }
      } catch (error) {
        console.error('Error fetching report details:', error);
      } finally {
        setIsLoading(false);
      }
    }, (error) => {
      console.error('Error listening for unread messages:', error);
      setIsLoading(false);
    });
    
    return () => unsubscribe();
  }, [user]);
  
  if (!visible || isLoading || !notification) return null;
  
  const handleViewReport = () => {
    // Navigate to the report with the correct reportId
    navigate(`/?reportId=${notification.reportId}`);
    setVisible(false);
  };
  
  return (
    <div className="fixed top-12 left-0 right-0 bg-gradient-to-r from-blue-500 to-cyan-400 text-white py-3 px-4 shadow-md z-[60]">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div 
          className="flex items-center space-x-2 flex-1 cursor-pointer" 
          onClick={handleViewReport}
        >
          <MessageCircle className="h-5 w-5" />
          <div className="flex-1">
            <p className="text-sm font-medium">
              Unread message from {notification.senderName} regarding {notification.clientName}'s report
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button 
            onClick={handleViewReport}
            className="px-3 py-1 bg-white text-blue-500 text-xs rounded hover:bg-blue-50 font-medium"
          >
            View
          </button>
          
          <button 
            onClick={() => setVisible(false)}
            className="p-1 rounded hover:bg-white/20"
            title="Dismiss notification"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationBar; 